import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { fetchColeccionesGlobales } from "../services/dataCache";
import LoadingSpinner from "./LoadingSpinner";
import EmptyState from "./EmptyState";
import { ReactComponent as ViticulturaIcon } from '../assets/icons/ViticulturaIcon.svg';
import { ReactComponent as VinificacionIcon } from '../assets/icons/VinificacionIcon.svg';
import { ReactComponent as EmbotelladoIcon } from '../assets/icons/EmbotelladoIcon.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/SearchIcon.svg';
import { ReactComponent as InventarioIcon } from '../assets/icons/InventarioIcon.svg';
import { ReactComponent as StockIcon } from '../assets/icons/StockIcon.svg';
import { ReactComponent as ClientesIcon } from '../assets/icons/ClientesIcon.svg';
import { ReactComponent as VentasIcon } from '../assets/icons/VentasIcon.svg';
import { ReactComponent as ComprasIcon } from '../assets/icons/ComprasIcon.svg';
const ITEMS_PER_BATCH = 10;
const Home = () => {
const { searchText } = useOutletContext();
const [items, setItems] = useState([]);
const [filteredItems, setFilteredItems] = useState([]);
const [recientes, setRecientes] = useState([]);
const [loading, setLoading] = useState(true);
const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
const navigate = useNavigate();
const [lastDoc, setLastDoc] = useState(null);
const [hasMore, setHasMore] = useState(true);
const tableRef = useRef(null);
const isLoadingMoreRef = useRef(false);
const coleccionesNombres = {
stock: "Stock",
clientes: "Clientes",
compras: "Compras",
ventas: "Ventas",
viticultura: "Viticultura",
vinificacion: "Vinificación",
embotellado: "Embotellado",
trazabilidad: "Trazabilidad",
inventario: "Inventario",
};
const coleccionIconos = {
viticultura: ViticulturaIcon,
vinificacion: VinificacionIcon,
embotellado: EmbotelladoIcon,
trazabilidad: SearchIcon,
stock: StockIcon,
clientes: ClientesIcon,
ventas: VentasIcon,
compras: ComprasIcon,
inventario: InventarioIcon,
};
const getDisplayValue = (doc) => {
let value = doc.id;
if (doc.coleccion === 'clientes') {
if (doc.nombre || doc.apellido) {
value = `${doc.nombre || ''} ${doc.apellido || ''}`.trim();
} else if (doc.razonSocial) {
value = doc.razonSocial;
}
} else if (doc.coleccion === 'inventario' && doc.fecha && typeof doc.fecha.toDate === 'function') {
const date = doc.fecha.toDate();
const day = String(date.getDate()).padStart(2, '0');
const month = String(date.getMonth() + 1).padStart(2, '0');
const year = date.getFullYear();
value = `Inventario ${day}/${month}/${year}`;
}
return value;
};
const handleSort = (key) => {
let direction = 'asc';
if (sortConfig.key === key && sortConfig.direction === 'asc') {
direction = 'desc';
}
setSortConfig({ key, direction });
};
const sortedItems = useMemo(() => {
if (!sortConfig.key) {
return filteredItems;
}
const sortableItems = [...filteredItems];
sortableItems.sort((a, b) => {
let aValue, bValue;
if (sortConfig.key === 'id') {
aValue = getDisplayValue(a);
bValue = getDisplayValue(b);
} else {
aValue = a[sortConfig.key];
bValue = b[sortConfig.key];
}
if (aValue === null || aValue === undefined) return 1;
if (bValue === null || bValue === undefined) return -1;
if (sortConfig.key === 'ultimaModificacion') {
const dateA = aValue instanceof Date ? aValue : new Date(aValue.seconds * 1000);
const dateB = bValue instanceof Date ? bValue : new Date(bValue.seconds * 1000);
if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
return 0;
}
if (typeof aValue === 'string' && typeof bValue === 'string') {
return aValue.localeCompare(bValue) * (sortConfig.direction === 'asc' ? 1 : -1);
} else {
if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
return 0;
}
});
return sortableItems;
}, [filteredItems, sortConfig]);
const handleRowClick = (id, coleccion) => {
let path;
switch (coleccion) {
case 'stock':
path = `/stock`;
break;
case 'clientes':
path = `/clientes/${id}`;
break;
case 'ventas':
path = `/ventas`;
break;
case 'inventario':
path = `/inventario/${id}`;
break;
case 'compras':
path = `/compras`;
break;
case 'vinificacion':
case 'viticultura':
case 'embotellado':
case 'trazabilidad':
path = `/trazabilidad/${id}`;
break;
default:
path = `/${coleccion}/${id}`;
break;
}
navigate(path);
};
useEffect(() => {
    const loadInitialData = async () => {
        setLoading(true);
        setItems([]);
        setLastDoc(null);
        setHasMore(true);
        try {
            await fetchItems(true);
        } catch (error) {
            console.error("Error al traer datos iniciales:", error);
        } finally {
            setLoading(false);
        }
    };
    loadInitialData();
}, []);
const fetchItems = async (isInitialLoad = false) => {
    if (!hasMore && !isInitialLoad) return;
    if (isLoadingMoreRef.current && !isInitialLoad) return;
    if (!isInitialLoad) isLoadingMoreRef.current = true;
    try {
        let todosDocs = [];
        let newLastDoc = lastDoc;
        let allCollectionsHaveMore = false;
        if (isInitialLoad) {
            // Datos del caché compartido con SearchBar: una sola descarga por sesión,
            // con las 9 colecciones pedidas en paralelo.
            const cacheDocs = await fetchColeccionesGlobales();
            todosDocs = cacheDocs.map((docData) => ({
                ...docData,
                ultimaModificacion: docData.ultimaActualizacion ||
                                    docData.fecha_modificacion ||
                                    docData.fechaCreacion ||
                                    docData.fecha_creacion ||
                                    docData.fecha ||
                                    docData.fechaVenta ||
                                    null,
            }));
            const itemsOrdenados = [...todosDocs].sort((a, b) => {
                const fa = a.ultimaModificacion;
                const fb = b.ultimaModificacion;
                if (!fa) return 1;
                if (!fb) return -1;
                return fb - fa;
            });
            const initialBatch = itemsOrdenados.slice(0, ITEMS_PER_BATCH);
            setItems(itemsOrdenados);
            setFilteredItems(initialBatch);
            setHasMore(itemsOrdenados.length > ITEMS_PER_BATCH);
            setLastDoc(ITEMS_PER_BATCH);
            setRecientes(itemsOrdenados.slice(0, 4));
        } else {
            const nextIndex = lastDoc;
            const nextBatch = items.slice(nextIndex, nextIndex + ITEMS_PER_BATCH);
            if (!searchText) {
                setFilteredItems(prev => [...prev, ...nextBatch]);
                setLastDoc(nextIndex + nextBatch.length);
                if (nextBatch.length < ITEMS_PER_BATCH || (nextIndex + nextBatch.length) >= items.length) {
                    setHasMore(false);
                }
            } else {
            }
        }
    } catch (error) {
        console.error("Error al traer datos de la tanda:", error);
    } finally {
        if (!isInitialLoad) isLoadingMoreRef.current = false;
    }
};
useEffect(() => {
    const lowerCaseSearch = searchText.toLowerCase();
    if (!searchText) {
        const initialBatch = items.slice(0, ITEMS_PER_BATCH);
        setRecientes(items.slice(0, 4));
        setFilteredItems(initialBatch);
        setLastDoc(initialBatch.length);
        setHasMore(items.length > ITEMS_PER_BATCH);
    } else {
        const results = items.filter(doc => {
            const docId = doc.id ? doc.id.toLowerCase() : '';
            const nombreCliente = doc.nombre ? doc.nombre.toLowerCase() : '';
            const apellidoCliente = doc.apellido ? doc.apellido.toLowerCase() : '';
            const razonSocialCliente = doc.razonSocial ? doc.razonSocial.toLowerCase() : '';
            const coleccionNombre = coleccionesNombres[doc.coleccion].toLowerCase();
            return (
                docId.includes(lowerCaseSearch) ||
                nombreCliente.includes(lowerCaseSearch) ||
                apellidoCliente.includes(lowerCaseSearch) ||
                razonSocialCliente.includes(lowerCaseSearch) ||
                coleccionNombre.includes(lowerCaseSearch)
            );
        });
        setRecientes(results.slice(0, 4));
        setFilteredItems(results);
        setHasMore(false); 
    }
}, [searchText, items]);
useEffect(() => {
    if (searchText || !hasMore) return;
    const tableElement = tableRef.current;
    if (!tableElement) return;
    const handleScroll = () => {
        const isAtBottom = tableElement.scrollTop + tableElement.clientHeight >= tableElement.scrollHeight - 100;
        if (isAtBottom && !isLoadingMoreRef.current && hasMore) {
            fetchItems();
        }
    };
    tableElement.addEventListener('scroll', handleScroll);
    return () => {
        tableElement.removeEventListener('scroll', handleScroll);
    };
}, [items.length, hasMore, lastDoc, searchText]);
if (loading) return <LoadingSpinner />;
const formatearFecha = (fecha) => {
if (!fecha) {
return "-";
}
let date;
if (typeof fecha.toDate === 'function') {
date = fecha.toDate();
} else if (fecha instanceof Date) {
date = fecha;
} else if (typeof fecha === 'string') {
date = new Date(fecha);
if (isNaN(date.getTime())) {
return "-";
}
} else {
return "-";
}
return `El ${date.toLocaleDateString("es-AR")} a las ${date.toLocaleTimeString("es-AR", {
hour: "2-digit",
minute: "2-digit",
hour12: false,
})}`;
};
const getIconComponent = (coleccion) => {
const IconComponent = coleccionIconos[coleccion];
if (!IconComponent) return null;
let extraClass = '';
if (coleccion === 'compras') {
extraClass = 'icon-compras';
} else if (coleccion === 'clientes') {
extraClass = 'icon-clientes';
} else if (coleccion === 'ventas') {
extraClass = 'icon-ventas';
} else if (coleccion === 'inventario') {
extraClass = 'icon-inventario';
}
return (
<div className={`icon-container ${extraClass}`}>
<IconComponent />
</div>
);
};
return (
<div className="Trazabilidad">
<div className="trazabilidad-content" ref={tableRef}>
{items.length === 0 && !searchText ? (
<EmptyState label="datos" />
) : (
<>
{!searchText && (
<div className="section">
<h2>Recientes</h2>
{recientes.length === 0 ? (
<p>No hay documentos recientes.</p>
) : (
<div className="recent-grid">
{recientes.map((doc) => {
const displayValue = getDisplayValue(doc); 
return (
<div
key={doc.id + doc.coleccion}
className="document-card recent-card"
onClick={() => handleRowClick(doc.id, doc.coleccion)}
style={{ flexDirection: "column", textAlign: "left", alignItems: "start", justifyContent: "start", gap: "2px", cursor: "pointer" }}
>
<div style={{ display: 'flex', alignItems: 'center' }}>
<span className="doc-id" style={{ textAlign: "left", margin: "0",maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
{displayValue}
</span>
</div>
<p className="card-creator" style={{ textAlign: "left", margin: "0" }}>
{doc.modificador || doc.creador || "Finca Los Astros"}
</p>
<p className="card-date" style={{ textAlign: "left", margin: "0" }}>{formatearFecha(doc.ultimaModificacion)}</p>
</div>
);
})}
</div>
)}
</div>
)}
<div className="section">
{searchText && (
<h2>Resultados</h2>
)}
<div className="home-table"> 
<div className="home-table-header">
<span className="home-table-column home-table-column-id" onClick={() => handleSort('id')} style={{ fontFamily: 'Effra' }}>
Código
{sortConfig.key === 'id' && (
<span className={`arrow ${sortConfig.direction}`}></span>
)}
</span>
<span className="home-table-column home-table-column-date" onClick={() => handleSort('ultimaModificacion')} style={{ fontFamily: 'Effra' }}>
Última Modificación
{sortConfig.key === 'ultimaModificacion' && (
<span className={`arrow ${sortConfig.direction}`}></span>
)}
</span>
<span className="home-table-column home-table-column-collection" onClick={() => handleSort('coleccion')} style={{ fontFamily: 'Effra' }}>
Colección
{sortConfig.key === 'coleccion' && (
<span className={`arrow ${sortConfig.direction}`}></span>
)}
</span>
</div>
{sortedItems.map((doc) => {
const displayValue = getDisplayValue(doc);
return (
<div
key={doc.id + doc.coleccion}
className="home-table-row"
style={{ cursor: "pointer" }}
onClick={() => handleRowClick(doc.id, doc.coleccion)}
>
<span className="home-table-column home-table-column-id" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Effra' }}>
{getIconComponent(doc.coleccion)}
<span style={{
display: "inline-block",
maxWidth: "300px",
overflow: "hidden",
textOverflow: "ellipsis",
whiteSpace: "nowrap",
textAlign: "left",
margin: 0,
}}>
{displayValue}
</span>
</span>
<span className="home-table-column home-table-column-date" style={{ fontFamily: 'Effra' }}>
<strong>
{doc.modificador || doc.creador || "Finca Los Astros"}
</strong><br />
{formatearFecha(doc.ultimaModificacion)}
</span>
<span className="home-table-column home-table-column-collection" style={{ fontFamily: 'Effra' }}>
{coleccionesNombres[doc.coleccion] || doc.coleccion}
</span>
</div>
);
})}
</div>
</div>
</>)}
</div>
</div>
);
};
export default Home;